/**
 * Classify a flight-sharing API failure into the handful of outcomes the UI
 * can say something useful about.
 *
 * **Why only some codes are translated.** Every error the backend raises
 * carries an RFC 7807 `detail` string, and for most failures that string is
 * already the best available message — re-wording it here would just create a
 * second place to keep in sync. The codes below earn a UI-owned message for
 * one of three specific reasons:
 *
 * - **`SHARE_NO_SOLD_DATE`** — a *deliberate* refusal, not a fault. The
 *   backend's message ends "or pass from_date explicitly", which names an API
 *   parameter the user cannot see. Worse, its other suggestion ("set the
 *   drone's sold_date") is currently unactionable: nothing in the product
 *   writes `fleet.drones.sold_date`. The UI has to point at the one control
 *   that does resolve it — the From-date field in the same dialog.
 * - **`SHARE_NO_FLIGHTS`** — an empty result, surfaced by the API as a 404.
 *   Rendering "not found" for "that date range contains no flights" reads as a
 *   bug.
 * - **`SHARE_NOT_HUB_AND_SPOKE` / `SHARE_SELF`** — unreachable through this UI,
 *   because the grantee picker is populated from
 *   `GET /api/flight-shares/organizations`, which only ever offers legal
 *   counterparties. They are classified anyway *because* they are unreachable:
 *   if one fires, the picker's guarantee has broken, and a generic "Validation
 *   failed" would hide that. They must name the rule that was violated.
 *
 * Everything else — `SHARE_ALREADY_EXISTS`, `SHARE_ALREADY_REVOKED`,
 * `SHARE_NOT_FOUND`, `DRONE_NOT_FOUND`, transport failures — falls through to
 * `generic`, and the caller shows the backend's own `detail` via
 * `getErrorMessage`. Those messages are already specific, already actionable
 * ("Re-run to grant the remainder"), and owning copies of them here would only
 * let the two drift apart.
 *
 * Pure and dependency-free on purpose: this is the one piece of the sharing UI
 * that can be unit-tested without a DOM, a query client or a network.
 */
import { extractErrorCode, extractErrorDetails } from '../../utils/errorHandler';
import { isAxiosErrorWithResponse } from '../../types/errors';

/**
 * True only for a genuine 403.
 *
 * The sharing surfaces lean on the counterparties call as a visible stand-in
 * for the org-admin gate: if it is refused, the caller may not grant or revoke,
 * and saying so is more useful than a raw error. But that reading is only
 * correct for a 403. A 500, a network drop, or the endpoint's own
 * `SHARE_OWN_ORG_NOT_FOUND` would otherwise be reported to a real admin as
 * "only organization admins can change sharing" — a confident, wrong
 * explanation that sends them to ask for permissions they already have.
 */
export function isForbiddenError(error: unknown): boolean {
  return isAxiosErrorWithResponse(error) && error.response?.status === 403;
}

export type ShareErrorKind =
  | 'noSoldDate'
  | 'noFlights'
  | 'noObjects'
  | 'illegalPairing'
  | 'generic';

export interface ShareErrorClassification {
  kind: ShareErrorKind;
  /**
   * The drone the failure is about, when the backend named one. Only ever read
   * from `details.drone_serial` — never parsed out of the message text, which
   * is free-form and translated.
   */
  droneSerial?: string;
}

/**
 * The error codes that get a UI-owned message, mapped to their kind.
 *
 * A `Record`, not a `switch`: it makes "which codes are special-cased" a single
 * readable list, and the exhaustive `ShareErrorKind` union keeps a typo from
 * silently inventing a new kind.
 */
const CLASSIFIED_CODES: Record<string, ShareErrorKind> = {
  SHARE_NO_SOLD_DATE: 'noSoldDate',
  SHARE_NO_FLIGHTS: 'noFlights',
  // The file/folder equivalent, and it means something sharper than its flight
  // counterpart: the objects were dropped because they are not the caller's to
  // share (the service filters on organization_id before writing), so the 404
  // is deliberately indistinguishable from "no such id". A generic "not found"
  // would read as a bug in the Files tab rather than as the refusal it is.
  SHARE_NO_OBJECTS: 'noObjects',
  SHARE_NOT_HUB_AND_SPOKE: 'illegalPairing',
  SHARE_SELF: 'illegalPairing',
};

export function classifyShareError(error: unknown): ShareErrorClassification {
  const code = extractErrorCode(error);
  const kind = (code && CLASSIFIED_CODES[code]) || 'generic';

  const details = extractErrorDetails(error);
  const rawSerial = details?.drone_serial;
  // `details` is `Record<string, unknown>`, so the serial is only trusted when
  // it really is a string. A non-string here would otherwise reach the message
  // interpolation as "[object Object]".
  const droneSerial = typeof rawSerial === 'string' && rawSerial ? rawSerial : undefined;

  return droneSerial ? { kind, droneSerial } : { kind };
}

/**
 * True when the failure means "nothing was shared, and that is fine" rather
 * than "the request failed".
 *
 * `SHARE_NO_FLIGHTS` is a 404, so the mutation rejects — but the honest
 * rendering is an informational note, not an error toast. Kept as a named
 * predicate so the dialogs cannot disagree about it.
 *
 * **`noObjects` is deliberately NOT benign**, despite being the same status
 * code on the same shape of endpoint. "That date range contains no flights" is
 * an empty result; "none of these objects are yours to share" is a refusal, and
 * the user needs to see it as one. The asymmetry is the point of listing them
 * separately rather than folding both into an `emptySelection` kind.
 */
export function isBenignShareOutcome(classification: ShareErrorClassification): boolean {
  return classification.kind === 'noFlights';
}
